import { useStoreApi } from '@givebacks/api/store';
import { UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { createQueryOptions } from 'core/Packages';

import { storeKeys } from '../../../query-key-factory';
import { PeerTypes } from '../../types';
import { CampaignPrize, CampaignPrizes } from '../../types/prizes';
import { useListCampaignPeers } from '../peers/get';
import { PrizeSearch } from './types';
import { addPrizeType } from './utils/prizeTypes';

const useCurrentCauseQueryString = (causeId?: string) => {
  return `cause_id=${causeId}`;
};

interface CampaignPrizeResponse {
  prize: CampaignPrize;
}
interface CampaignPrizesResponse {
  prizes: CampaignPrizes;
}

type GetCampaignsPrizesRequest = {
  meta?: {
    campaignId?: string;
    causeId?: string;
    queryString?: string;
  };
};

const useGetCampaignPrizesByType = () => {
  const StoreApi = useStoreApi();
  const queryClient = useQueryClient();
  return async (context: GetCampaignsPrizesRequest) => {
    const { campaignId, causeId, queryString } = context.meta || {};
    const { data } = await StoreApi.get<CampaignPrizesResponse>(
      `/store_items/${campaignId}/prizes?${queryString}`
    );
    data.prizes.forEach((prize) => {
      if (!causeId || !campaignId) return;
      queryClient.setQueryData(
        storeKeys.id(causeId).items.campaigns.id(campaignId).prizes.id(prize.uuid).detail,
        addPrizeType(prize)
      );
    });
    return data.prizes;
  };
};

const useShowCampaignPrize = ({
  causeId,
  campaignId,
  prizeId,
}: {
  causeId?: string;
  campaignId?: string;
  prizeId?: string;
}) => {
  const StoreApi = useStoreApi();
  const causeQuery = useCurrentCauseQueryString(causeId);

  const getCampaignPrize = async () => {
    const { data } = await StoreApi.get<CampaignPrizeResponse>(
      `/store_items/${campaignId}/prizes/${prizeId}?${causeQuery}`
    );
    return addPrizeType(data.prize);
  };

  const key =
    campaignId && causeId && prizeId
      ? [...storeKeys.id(causeId).items.campaigns.id(campaignId).prizes.id(prizeId).detail]
      : undefined;

  return useQuery({
    queryKey: key ?? [],
    queryFn: getCampaignPrize,
    enabled: !!campaignId && !!causeId && !!prizeId,
  });
};

const useListCampaignPrize = ({
  causeId,
  campaignId,
  queryOptions,
}: {
  queryOptions?: PrizeSearch;
  causeId?: string;
  campaignId?: string;
}) => {
  const causeQuery = useCurrentCauseQueryString(causeId);

  const getCampaignPrizesByType = useGetCampaignPrizesByType();
  const { queryString, queryObject } = createQueryOptions<PrizeSearch>({
    search: queryOptions,
    existingSearchString: causeQuery,
  });
  const key =
    campaignId && causeId
      ? [...storeKeys.id(causeId).items.campaigns.id(campaignId).prizes.all, queryObject]
      : undefined;
  return useQuery({
    queryKey: key ?? [],
    queryFn: getCampaignPrizesByType,
    enabled: !!campaignId && !!causeId,
    meta: { campaignId, causeId, queryString },
  });
};

const usePrefetchCampaignPrizes_new = ({ causeId, campaignId }) => {
  const queryClient = useQueryClient();
  const causeQuery = useCurrentCauseQueryString(causeId);

  const getCampaignPrizesByType = useGetCampaignPrizesByType();
  const { queryString: teamQs, queryObject: teamQo } = createQueryOptions<PrizeSearch>({
    search: {
      searches: {
        peer_type: {
          value: 'team',
        },
      },
    },
    existingSearchString: causeQuery,
  });
  const { queryString: participantQs, queryObject: participantQo } =
    createQueryOptions<PrizeSearch>({
      search: {
        searches: {
          peer_type: {
            value: 'participant',
          },
        },
      },
      existingSearchString: causeQuery,
    });
  const prefetch = async () => {
    if (!campaignId || !causeId) return;
    await queryClient.prefetchQuery({
      queryKey: [...storeKeys.id(causeId).items.campaigns.id(campaignId).prizes.all, teamQo],
      queryFn: getCampaignPrizesByType,
      meta: { campaignId, causeId, queryString: teamQs },
    });
    await queryClient.prefetchQuery({
      queryKey: [...storeKeys.id(causeId).items.campaigns.id(campaignId).prizes.all, participantQo],
      queryFn: getCampaignPrizesByType,
      meta: { campaignId, causeId, queryString: participantQs },
    });
  };

  return prefetch;
};

const useGetPeerPrizes = <T extends PeerTypes>({
  peerType,
  campaignId,
  causeId,
}: {
  peerType: T;
  campaignId?: string;
  causeId?: string;
}) => {
  return useListCampaignPeers<T>({
    campaignId,
    causeId,
    queryOptions: { options: { prize_report: 'true' } },
    select: (data) => data?.[peerType as 'participants' | 'teams']?.[0]?.prize_reports ?? [],
    peer_type: peerType,
  }) as unknown as UseQueryResult<CampaignPrize[]>; //last resort for now, we'll have to cast it to the correct type
};
export {
  useGetPeerPrizes,
  useListCampaignPrize,
  usePrefetchCampaignPrizes_new,
  useShowCampaignPrize
};

